import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Page } from "@ui/page/Page";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Building2, Network, Plug, ShieldCheck, UserCheck, Wallet } from "lucide-react";
import { useState } from "react";
import { ChannelsDirectory, type TChannelsPreset } from "../providers/ChannelsDirectory";
import { PaymentMethods } from "../providers/PaymentMethods";
import { ProvidersList } from "../providers/ProvidersList";
import { useProvidersDirectory } from "../providers/useProvidersDirectory";
import { AcquirerBeneficiaries } from "./AcquirerBeneficiaries";
import { IntegrationsList } from "./IntegrationsList";

export { PaymentProviderEditor } from "../providers/ProvidersList";

enum TAB {
	PROVIDERS = 0,
	CHANNELS = 1,
	INTEGRATIONS = 2,
	BENEFICIARIES = 3,
	METHODS = 4,
}

const counted = (title: string, list: { loading: boolean; items: unknown[] }) => (list.loading ? title : `${title} · ${list.items.length}`);

// Payment providers and everything they are made of: channels, integrations, beneficiaries and methods.
export const AcquirersPage = () => {
	useLocale();
	const directory = useProvidersDirectory();
	const [tab, setTab] = useState<number>(TAB.PROVIDERS);
	// A preset opens the Channels tab already filtered, e.g. from a method's channel count.
	const [preset, setPreset] = useState<{ value: TChannelsPreset; key: number } | null>(null);
	const showChannels = (value: TChannelsPreset) => {
		setPreset({ value, key: Date.now() });
		setTab(TAB.CHANNELS);
	};
	const { providers, channels, integrations, methods } = directory;

	return (
		<Page label={msg("providers")} description={msg("whoProcessesYourPaymentsThroughWhichChannelsIntegrationsAndMethods")} icon={<ShieldCheck size={20} />}>
			<Tabs
				variant="modern"
				selected={tab}
				onSectionSelect={section => {
					if (section !== TAB.CHANNELS) setPreset(null);
					setTab(section);
				}}>
				<TabItem title={counted(msg("providers"), providers)} icon={<Building2 size={16} />}>
					<ProvidersList
						providers={providers.items}
						channels={channels.items}
						loading={providers.loading}
						busy={providers.busy || channels.busy}
						error={providers.error}
						onReload={() => {
							providers.reload();
							channels.reload();
						}}
					/>
				</TabItem>
				<TabItem title={counted(msg("channels"), channels)} icon={<Network size={16} />}>
					<ChannelsDirectory
						key={preset?.key ?? "all"}
						channels={channels.items}
						providers={providers.items}
						methods={methods.items}
						integrations={integrations.items}
						loading={channels.loading}
						busy={channels.busy}
						error={channels.error}
						truncated={channels.truncated}
						preset={preset?.value}
						onReload={directory.reloadAll}
					/>
				</TabItem>
				<TabItem title={counted(msg("integrations"), integrations)} icon={<Plug size={16} />}>
					<IntegrationsList
						integrations={integrations.items}
						channels={channels.items}
						methods={methods.items}
						loading={integrations.loading}
						busy={integrations.busy || channels.busy}
						error={integrations.error}
						onReload={() => {
							integrations.reload();
							channels.reload();
						}}
						onShowChannels={integration => showChannels({ integration })}
					/>
				</TabItem>
				<TabItem title={msg("beneficiaries")} icon={<UserCheck size={16} />}>
					<AcquirerBeneficiaries />
				</TabItem>
				<TabItem title={counted(msg("methods"), methods)} icon={<Wallet size={16} />}>
					<PaymentMethods
						methods={methods.items}
						channels={channels.items}
						loading={methods.loading}
						busy={methods.busy || channels.busy}
						error={methods.error}
						onReload={() => {
							methods.reload();
							channels.reload();
						}}
						onShowChannels={method => showChannels({ method })}
					/>
				</TabItem>
			</Tabs>
		</Page>
	);
};
